import React from "react";
import { ArrowIcon } from "./ArrowIcon";

interface OrderCardProps {
  orderId: string;
  status: string;
  dateTime: string;
  onTrackOrder?: () => void;
  onClick?: () => void;
  progress?: number; 
  /** Highlights that the customer needs to do something (approve, pay) */
  actionLabel?: string;
}

const statusSteps = [
  "open",
  "ready_to_pickup",
  "collected",
  "received",
  "processing",
  "ready_for_delivery",
  "delivered",
];

const toKey = (status: string) =>
  status.trim().toLowerCase().replace(/\s+/g, "_");

const statusToStep = (status: string) => {
  const idx = statusSteps.findIndex((step) => step === toKey(status));
  return idx === -1 ? 0 : idx;
};

const statusLabel = (status: string) => {
  const key = toKey(status);
  if (key === "open") return "Open";
  if (key === "ready_to_pickup") return "Ready to Pickup";
  if (key === "collected") return "Collected";
  if (key === "received") return "Received";
  if (key === "processing") return "Processing";
  if (key === "ready_for_delivery") return "Ready for Delivery";
  if (key === "delivered") return "Delivered";
  return status;
};

export const OrderCard: React.FC<OrderCardProps> = ({
  orderId,
  status,
  dateTime,
  onTrackOrder,
  onClick,
  progress,
  actionLabel,
}) => {
  const step =
    progress !== undefined
      ? Math.round(progress * (statusSteps.length - 1))
      : statusToStep(status);
  return (
    <div
      className="rounded-2xl px-5 py-4 mb-2 shadow-sm bg-ordercard"
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {actionLabel && (
        <span className="inline-block mb-2 text-[11px] font-bold uppercase tracking-wide text-white bg-orange-500 rounded-full px-2.5 py-0.5">
          {actionLabel}
        </span>
      )}
      <div className="flex justify-between items-center mb-2">
        <span className="text-md text-black ">
          <span className="font-normal text-base text-gray-700">{orderId}</span>
        </span>
        <button
          className="text-primary font-semibold text-base flex items-center gap-1"
          onClick={(e) => {
            e.stopPropagation();
            onTrackOrder?.();
          }}
        >
          Track Order <ArrowIcon />
        </button>
      </div>
      {/* Progress bar */}
      <div className="flex gap-2 mb-3">
        {statusSteps.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full flex-1 ${
              i <= step ? "bg-primary" : "bg-white"
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="font-bold text-lg text-black mr-2">
          {statusLabel(status)}
        </span>
        <span className="text-gray-500 text-base">{dateTime}</span>
      </div>
    </div>
  );
};
